package org.gymly.ui.admin

import androidx.compose.material3.minimumInteractiveComponentSize
import org.gymly.util.GymlyCurrency

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminBillingPeriod
import org.gymly.model.AdminCatalogService
import org.gymly.model.AdminExchangeRateQuote
import org.gymly.model.AdminMembershipPlanItem
import org.gymly.model.AdminMembershipPlanUpsert
import org.gymly.model.AdminScheduleType
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.util.formatMoney
import org.gymly.util.userMessage
import kotlin.math.roundToLong
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant
import org.gymly.ui.components.VerticalScrollColumn

private enum class PlansDateFilter(val label: String, val days: Int?) {
    Days7("7 días", 7),
    Days30("30 días", 30),
    All("Todos", null),
}

private sealed class PlansDialog {
    data object Create : PlansDialog()
    data class Edit(val plan: AdminMembershipPlanItem) : PlansDialog()
    data class Detail(val plan: AdminMembershipPlanItem) : PlansDialog()
    data class Delete(val plan: AdminMembershipPlanItem) : PlansDialog()
}

private fun moneyToCents(raw: String): Long? {
    val cleaned = raw.trim().replace("€", "").replace(" ", "").replace(",", ".")
    if (cleaned.isEmpty()) return 0L
    val value = cleaned.toDoubleOrNull() ?: return null
    if (value < 0) return null
    return (value * 100.0).roundToLong()
}

private fun centsToEurosInput(cents: Long): String {
    val scaled = (cents)
    val intPart = scaled / 100
    val frac = kotlin.math.abs(scaled % 100).toInt()
    return "$intPart,${frac.toString().padStart(2, '0')}"
}

@Composable
fun AdminMembershipPlansScreen() {
    val scope = rememberCoroutineScope()
    var plans by remember { mutableStateOf<List<AdminMembershipPlanItem>>(emptyList()) }
    var services by remember { mutableStateOf<List<AdminCatalogService>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var dateFilter by remember { mutableStateOf(PlansDateFilter.All) }
    var dialog by remember { mutableStateOf<PlansDialog?>(null) }
    var saving by remember { mutableStateOf(false) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                plans = AdminRepo.api.listMembershipPlans()
                services = AdminRepo.api.listServices().filter { it.active }
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar los planes.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(LocalAdminDataRevision.current) { reload() }

    val filtered = remember(plans, query, dateFilter) {
        val q = query.trim().lowercase()
        val cutoff = dateFilter.days?.let { Clock.System.now() - it.days }
        plans.filter { p ->
            val matchesQuery = q.isEmpty() ||
                p.name.lowercase().contains(q) ||
                p.billingPeriod.labelEs().lowercase().contains(q) ||
                p.scheduleType.labelEs().lowercase().contains(q)
            val matchesDate = if (cutoff == null) {
                true
            } else {
                val created = p.createdAt?.let { runCatching { Instant.parse(it) }.getOrNull() }
                created == null || created >= cutoff
            }
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "name",
        resetKey = listOf(query, dateFilter),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "schedule" -> { p -> p.scheduleType.labelEs().lowercase() }
                "price" -> { p -> p.priceCents }
                "active" -> { p -> if (p.active) 1 else 0 }
                else -> { p -> p.name.lowercase() }
            }
        }
    }

    val total = filtered.size
    val weekly = filtered.count { it.billingPeriod == AdminBillingPeriod.WEEKLY }
    val monthly = filtered.count { it.billingPeriod == AdminBillingPeriod.MONTHLY }
    val daily = filtered.count { it.billingPeriod == AdminBillingPeriod.DAILY }
    val active = filtered.count { it.active }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        Column(Modifier.fillMaxSize()) {
            PlansHeader(dateFilter) { dateFilter = it }
            Spacer(Modifier.height(16.dp))
            PlansKpiRow(total, weekly, monthly, daily, active, compact)
            Spacer(Modifier.height(16.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            PlansDirectoryCard(
                plans = pageItems,
                totalCount = filtered.size,
                listPaging = listPaging,
                loading = loading,
                query = query,
                onQueryChange = { query = it },
                compact = compact,
                onCreate = { dialog = PlansDialog.Create },
                onView = { dialog = PlansDialog.Detail(it) },
                onEdit = { dialog = PlansDialog.Edit(it) },
                onDelete = { dialog = PlansDialog.Delete(it) },
            )
        }
    }

    when (val d = dialog) {
        is PlansDialog.Detail -> PlanDetailDialog(d.plan) { dialog = null }
        is PlansDialog.Create -> PlanFormDialog(
            title = "Agregar plan",
            initial = null,
            services = services,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.createMembershipPlan(upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo guardar el plan.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is PlansDialog.Edit -> PlanFormDialog(
            title = "Editar plan",
            initial = d.plan,
            services = services,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.updateMembershipPlan(d.plan.id, upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo guardar el plan.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is PlansDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text("Eliminar plan") },
            text = {
                Text("¿Seguro que deseas eliminar «${d.plan.name}»? Esta acción no se puede deshacer.")
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteMembershipPlan(d.plan.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo eliminar el plan.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Eliminar")
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun PlansHeader(
    dateFilter: PlansDateFilter,
    onFilterChange: (PlansDateFilter) -> Unit,
) {
    var menuOpen by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Text("Planes de Membresía", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                "Planes en venta desde /api/membership-plans (nombre, periodo, precio, activo).",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        Box {
            OutlinedButton(onClick = { menuOpen = true }, shape = RoundedCornerShape(10.dp)) {
                Text(dateFilter.label, color = AdminColors.Text)
            }
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                PlansDateFilter.entries.forEach { opt ->
                    DropdownMenuItem(
                        text = { Text(opt.label) },
                        onClick = {
                            onFilterChange(opt)
                            menuOpen = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun PlansKpiRow(
    total: Int,
    weekly: Int,
    monthly: Int,
    daily: Int,
    active: Int,
    compact: Boolean,
) {
    val items = listOf(
        Triple("Total de planes", total.toString(), AdminColors.Primary),
        Triple("Diarios", daily.toString(), Color(0xFF0288D1)),
        Triple("Semanales", weekly.toString(), Color(0xFFF59E0B)),
        Triple("Mensuales", monthly.toString(), Color(0xFF2E7D32)),
        Triple("Activos", active.toString(), Color(0xFF1565C0)),
    )
    if (compact) {
        // Celular: una franja de indicadores de una línea, para dejar el espacio a la lista.
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            items.forEach { (label, value, accent) ->
                AdminCompactKpiChip(label, value, accent, Modifier.weight(1f))
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            items.forEach { (label, value, accent) ->
                PlanKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun PlanKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun ColumnScope.PlansDirectoryCard(
    plans: List<AdminMembershipPlanItem>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    onCreate: () -> Unit,
    onView: (AdminMembershipPlanItem) -> Unit,
    onEdit: (AdminMembershipPlanItem) -> Unit,
    onDelete: (AdminMembershipPlanItem) -> Unit,
) {
    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("Planes de membresía", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                PlanSearchField(query, onQueryChange)
                if (canWriteModule(AdminModuleAccess.MEMBERSHIP_PLANS)) Button(
                    onClick = onCreate,
                    modifier = Modifier.fillMaxWidth().height(44.dp),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("+ Nuevo plan", fontWeight = FontWeight.SemiBold)
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Planes de membresía", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 220.dp)) {
                        PlanSearchField(query, onQueryChange)
                    }
                    if (canWriteModule(AdminModuleAccess.MEMBERSHIP_PLANS)) Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text("+ Nuevo plan", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text("No hay planes todavía.", color = AdminColors.TextMuted)
            }
            compact -> Column(Modifier.weight(1f).fillMaxWidth()) {
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    plans.forEach { p -> PlanMobileCard(p, onView, onEdit, onDelete) }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                PlansTable(
                    plans,
                    listPaging = listPaging,
                    onView,
                    onEdit,
                    onDelete,
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun PlanSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun PlansTable(
    plans: List<AdminMembershipPlanItem>,
    listPaging: AdminListPaging,
    onView: (AdminMembershipPlanItem) -> Unit,
    onEdit: (AdminMembershipPlanItem) -> Unit,
    onDelete: (AdminMembershipPlanItem) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AdminSortableHeader("Plan", "name", listPaging.sort, listPaging::onSort, Modifier.weight(1.6f))
            AdminSortableHeader("Horario", "schedule", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
            AdminSortableHeader("Precio", "price", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
            AdminSortableHeader("Activo", "active", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
            AdminListHeaderLabel("Acciones", Modifier.width(140.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth(),
        ) {
            plans.forEach { p ->
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Row(Modifier.weight(1.6f), verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(Color(p.billingPeriod.dotColorArgb())),
                        )
                        Spacer(Modifier.width(10.dp))
                        Column {
                            Text(p.name, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                            Text(p.billingPeriod.labelEs(), color = AdminColors.TextMuted, fontSize = 12.sp)
                        }
                    }
                    Box(Modifier.weight(0.9f)) { ScheduleBadge(p.scheduleType) }
                    Column(Modifier.weight(1f)) {
                        if (p.priceUsdCents > 0) {
                            Text(
                                formatMoney(p.priceUsdCents.toInt().coerceAtLeast(0), "USD"),
                                color = AdminColors.Text,
                                fontSize = 13.sp,
                            )
                        }
                        Text(
                            formatMoney(p.priceCents.toInt().coerceAtLeast(0), GymlyCurrency.CODE),
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                    }
                    Box(Modifier.weight(0.8f)) { PlanActiveBadge(p.active) }
                    Row(Modifier.width(140.dp)) {
                        IconButton(onClick = { onView(p) }) {
                            Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
                        }
                        if (canWriteModule(AdminModuleAccess.MEMBERSHIP_PLANS)) IconButton(onClick = { onEdit(p) }) {
                            Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.Primary)
                        }
                        if (canDeleteModule(AdminModuleAccess.MEMBERSHIP_PLANS)) IconButton(onClick = { onDelete(p) }) {
                            Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                        }
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
    }
}

@Composable
private fun PlanMobileCard(
    plan: AdminMembershipPlanItem,
    onView: (AdminMembershipPlanItem) -> Unit,
    onEdit: (AdminMembershipPlanItem) -> Unit,
    onDelete: (AdminMembershipPlanItem) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(14.dp),
    ) {
        Text(plan.name, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
        Text(plan.billingPeriod.labelEs(), color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(8.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
            ScheduleBadge(plan.scheduleType)
            Column {
                if (plan.priceUsdCents > 0) {
                    Text(
                        formatMoney(plan.priceUsdCents.toInt().coerceAtLeast(0), "USD"),
                        color = AdminColors.Text,
                        fontSize = 13.sp,
                    )
                }
                Text(
                    formatMoney(plan.priceCents.toInt().coerceAtLeast(0), GymlyCurrency.CODE),
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
            PlanActiveBadge(plan.active)
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            IconButton(onClick = { onView(plan) }) {
                Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted)
            }
            if (canWriteModule(AdminModuleAccess.MEMBERSHIP_PLANS)) IconButton(onClick = { onEdit(plan) }) {
                Icon(Icons.Outlined.Edit, null, tint = AdminColors.Primary)
            }
            if (canDeleteModule(AdminModuleAccess.MEMBERSHIP_PLANS)) IconButton(onClick = { onDelete(plan) }) {
                Icon(Icons.Outlined.Delete, null, tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun ScheduleBadge(type: AdminScheduleType) {
    Text(
        type.labelEs(),
        color = Color(type.badgeFgArgb()),
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(Color(type.badgeBgArgb()))
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

@Composable
private fun PlanActiveBadge(active: Boolean) {
    val bg = if (active) Color(0xFFE8F5E9) else Color(0xFFF3F4F6)
    val fg = if (active) Color(0xFF2E7D32) else AdminColors.TextMuted
    Text(
        if (active) "Activo" else "Inactivo",
        color = fg,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(bg)
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

@Composable
private fun PlanDetailDialog(plan: AdminMembershipPlanItem, onDismiss: () -> Unit) {
    var loaded by remember(plan.id) { mutableStateOf<AdminMembershipPlanItem?>(null) }
    var loading by remember(plan.id) { mutableStateOf(true) }

    LaunchedEffect(plan.id) {
        loading = true
        loaded = runCatching { AdminRepo.api.getMembershipPlan(plan.id) }.getOrElse { plan }
        loading = false
    }

    val detail = loaded ?: plan

    Dialog(onDismissRequest = onDismiss) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            Column(
                Modifier
                    .widthIn(max = 480.dp)
                    .wrapContentHeight()
                    .clip(RoundedCornerShape(16.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(16.dp),
            ) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Text("Detalle del plan", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                    Icon(
                        Icons.Outlined.Close,
                        contentDescription = "Cerrar",
                        tint = AdminColors.TextMuted,
                        modifier = Modifier.minimumInteractiveComponentSize().clickable(onClick = onDismiss).size(22.dp),
                    )
                }
                Spacer(Modifier.height(12.dp))
                if (loading) {
                    Box(Modifier.fillMaxWidth().padding(16.dp), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = AdminColors.Primary)
                    }
                } else {
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                    ) {
                        Column(Modifier.weight(1f)) {
                            PlanDetailRow("Nombre", detail.name)
                            PlanDetailRow("Periodo de facturación", detail.billingPeriod.labelEs())
                            if (detail.priceUsdCents > 0) {
                                PlanDetailRow(
                                    "Precio ($)",
                                    formatMoney(detail.priceUsdCents.toInt().coerceAtLeast(0), "USD"),
                                )
                            }
                            PlanDetailRow(
                                "Precio (Bs.)",
                                formatMoney(detail.priceCents.toInt().coerceAtLeast(0), GymlyCurrency.CODE),
                            )
                        }
                        Column(Modifier.weight(1f)) {
                            PlanDetailRow("Horario", detail.scheduleType.labelEs())
                            PlanDetailRow("Estado", if (detail.active) "Activo" else "Inactivo")
                            PlanDetailRow(
                                "Avisos vencimiento",
                                detail.reminderLeadDays?.takeIf { it.isNotBlank() }
                                    ?: "Default (${detail.effectiveReminderLeadDays ?: detail.billingPeriod.defaultReminderLeadDays()})",
                            )
                            PlanDetailRow(
                                "Servicios incluidos",
                                if (detail.services.isEmpty()) {
                                    "Todos (sin restricción)"
                                } else {
                                    detail.services.joinToString("\n") { "• ${it.name} (${it.type.labelEs()})" }
                                },
                            )
                        }
                    }
                }
                Spacer(Modifier.height(8.dp))
                Button(
                    onClick = onDismiss,
                    modifier = Modifier.align(Alignment.End),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("Cerrar")
                }
            }
        }
    }
}

@Composable
private fun PlanDetailRow(label: String, value: String) {
    Column(Modifier.padding(bottom = 8.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun PlanFormDialog(
    title: String,
    initial: AdminMembershipPlanItem?,
    services: List<AdminCatalogService>,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (AdminMembershipPlanUpsert) -> Unit,
) {
    var loadedInitial by remember(initial?.id) { mutableStateOf<AdminMembershipPlanItem?>(initial) }
    var loadingInitial by remember(initial?.id) { mutableStateOf(initial?.id != null) }

    LaunchedEffect(initial?.id) {
        val id = initial?.id
        if (id == null) {
            loadedInitial = null
            loadingInitial = false
        } else {
            loadingInitial = true
            loadedInitial = runCatching { AdminRepo.api.getMembershipPlan(id) }.getOrElse { initial }
            loadingInitial = false
        }
    }

    val source = loadedInitial
    var name by remember(source) { mutableStateOf(source?.name.orEmpty()) }
    var period by remember(source) { mutableStateOf(source?.billingPeriod ?: AdminBillingPeriod.MONTHLY) }
    var schedule by remember(source) { mutableStateOf(source?.scheduleType ?: AdminScheduleType.MIXED) }
    var priceUsd by remember(source) {
        mutableStateOf(source?.takeIf { it.priceUsdCents > 0 }?.let { centsToEurosInput(it.priceUsdCents) }.orEmpty())
    }
    var priceBs by remember(source) {
        mutableStateOf(source?.let { centsToEurosInput(it.priceCents) }.orEmpty())
    }
    var active by remember(source) { mutableStateOf(source?.active ?: true) }
    var reminderLeadDays by remember(source) { mutableStateOf(source?.reminderLeadDays.orEmpty()) }
    var selectedServiceIds by remember(source) {
        mutableStateOf(source?.services?.map { it.id }?.toSet() ?: emptySet())
    }
    var formError by remember { mutableStateOf<String?>(null) }
    var periodMenu by remember { mutableStateOf(false) }
    var scheduleMenu by remember { mutableStateOf(false) }
    var exchangeQuote by remember { mutableStateOf<AdminExchangeRateQuote?>(null) }
    val focusName = remember { FocusRequester() }
    val focusPriceUsd = remember { FocusRequester() }
    val focusPriceBs = remember { FocusRequester() }

    LaunchedEffect(Unit) {
        // Con "Usar tasa del BCV en divisas" apagado, el precio en $ y en Bs se escribe a mano: no se pide la cotización.
        val calcOn = runCatching { AdminRepo.api.getSettingsModules().exchangeRatesEnabled }.getOrDefault(false)
        exchangeQuote = if (calcOn) runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull() else null
        runCatching { focusName.requestFocus() }
    }

    LaunchedEffect(source?.id, source?.priceCents, source?.priceUsdCents, exchangeQuote) {
        val plan = source ?: return@LaunchedEffect
        if (priceUsd.isNotBlank()) return@LaunchedEffect
        if (plan.priceUsdCents > 0) {
            priceUsd = centsToEurosInput(plan.priceUsdCents)
            return@LaunchedEffect
        }
        if (plan.priceCents <= 0 || exchangeQuote == null) return@LaunchedEffect
        val (usd, _) = syncDualCurrencyFromVes(centsToEurosInput(plan.priceCents), exchangeQuote)
        if (usd.isNotBlank()) priceUsd = usd.replace('.', ',')
    }

    Dialog(onDismissRequest = onDismiss) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            Column(
                Modifier
                    .widthIn(max = 640.dp)
                    .heightIn(max = 720.dp)
                    .clip(RoundedCornerShape(16.dp))
                    .background(AdminColors.SurfaceContainer)
                    .padding(16.dp),
            ) {
            // Solo el formulario se desplaza; los botones quedan siempre visibles abajo.
            Column(
                Modifier
                    .weight(1f, fill = false)
                    .verticalScroll(rememberScrollState()),
            ) {
            Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(12.dp))
            if (loadingInitial) {
                Box(Modifier.fillMaxWidth().padding(24.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = AdminColors.Primary)
                }
            } else {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.Top,
                ) {
                    Column(Modifier.weight(1f)) {
                        PlanFormLabel("Nombre *")
                        PlanFormField(
                            name,
                            { name = it },
                            "Nombre del plan",
                            focusRequester = focusName,
                            onNext = { focusPriceUsd.requestFocus() },
                            onPrev = { },
                        )
                    }
                    Column(Modifier.weight(1f)) {
                        PlanFormLabel("Precio ($)")
                        PlanFormField(
                            priceUsd,
                            { raw ->
                                priceUsd = raw
                                val (_, ves) = syncDualCurrencyFromUsd(raw, exchangeQuote)
                                if (ves.isNotBlank()) {
                                    priceBs = ves.replace('.', ',')
                                }
                            },
                            "45,00",
                            focusRequester = focusPriceUsd,
                            onNext = { focusPriceBs.requestFocus() },
                            onPrev = { focusName.requestFocus() },
                            keyboardType = KeyboardType.Decimal,
                        )
                    }
                }
                Spacer(Modifier.height(8.dp))
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.Top,
                ) {
                    Column(Modifier.weight(1f)) {
                        PlanFormLabel("Periodo de facturación")
                        Box {
                            OutlinedButton(
                                onClick = { periodMenu = true },
                                modifier = Modifier.fillMaxWidth().height(56.dp),
                            ) {
                                Text(period.labelEs(), color = AdminColors.Text)
                            }
                            DropdownMenu(expanded = periodMenu, onDismissRequest = { periodMenu = false }) {
                                AdminBillingPeriod.choosable().forEach { p ->
                                    DropdownMenuItem(
                                        text = { Text(p.labelEs()) },
                                        onClick = {
                                            period = p
                                            periodMenu = false
                                        },
                                    )
                                }
                            }
                        }
                    }
                    Column(Modifier.weight(1f)) {
                        PlanFormLabel("Precio (Bs.)")
                        PlanFormField(
                            priceBs,
                            { priceBs = it },
                            "45,00",
                            focusRequester = focusPriceBs,
                            onNext = { },
                            onPrev = { focusPriceUsd.requestFocus() },
                            imeAction = ImeAction.Done,
                            keyboardType = KeyboardType.Decimal,
                        )
                    }
                }
                Spacer(Modifier.height(8.dp))
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(Modifier.weight(1f)) {
                        PlanFormLabel("Horario")
                        Box {
                            OutlinedButton(
                                onClick = { scheduleMenu = true },
                                modifier = Modifier.fillMaxWidth().height(56.dp),
                            ) {
                                Text(schedule.labelEs(), color = AdminColors.Text)
                            }
                            DropdownMenu(expanded = scheduleMenu, onDismissRequest = { scheduleMenu = false }) {
                                AdminScheduleType.entries.forEach { s ->
                                    DropdownMenuItem(
                                        text = { Text(s.labelEs()) },
                                        onClick = {
                                            schedule = s
                                            scheduleMenu = false
                                        },
                                    )
                                }
                            }
                        }
                    }
                    Column(Modifier.weight(1f)) {
                        val quote = exchangeQuote
                        Text(
                            if (quote?.usdRate != null) {
                                "Tasa BCV · ${formatExchangeRate(quote.usdRate)} Bs/\$" +
                                    (quote.effectiveDate?.let { " · $it" } ?: "")
                            } else {
                                "Cotización no disponible. Puedes indicar Bs. a mano."
                            },
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                    }
                }
                Spacer(Modifier.height(8.dp))
                PlanFormLabel("Servicios incluidos")
                Text(
                    "Si no seleccionas ninguno, el plan permite todos los servicios (comportamiento anterior).",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(bottom = 8.dp),
                )
                if (services.isEmpty()) {
                    Text("No hay servicios activos en el catálogo.", color = AdminColors.TextMuted, fontSize = 13.sp)
                } else {
                    // Celular: un servicio por fila; escritorio: dos.
                    BoxWithConstraints(Modifier.fillMaxWidth()) {
                    val perRow = if (maxWidth < 420.dp) 1 else 2
                    Column {
                    services.chunked(perRow).forEach { rowServices ->
                        Row(Modifier.fillMaxWidth()) {
                            rowServices.forEach { service ->
                                Row(
                                    Modifier
                                        .weight(1f)
                                        .clickable {
                                            selectedServiceIds = if (selectedServiceIds.contains(service.id)) {
                                                selectedServiceIds - service.id
                                            } else {
                                                selectedServiceIds + service.id
                                            }
                                        }
                                        .padding(vertical = 4.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                ) {
                                    Checkbox(
                                        checked = selectedServiceIds.contains(service.id),
                                        onCheckedChange = { checked ->
                                            selectedServiceIds = if (checked) {
                                                selectedServiceIds + service.id
                                            } else {
                                                selectedServiceIds - service.id
                                            }
                                        },
                                    )
                                    Text(
                                        "${service.name} (${service.type.labelEs()})",
                                        color = AdminColors.Text,
                                        fontSize = 13.sp,
                                    )
                                }
                            }
                            if (rowServices.size == 1) Spacer(Modifier.weight(1f))
                        }
                    }
                    }
                    }
                }
                Spacer(Modifier.height(10.dp))
                PlanFormLabel("Avisos de vencimiento (días antes)")
                Text(
                    "Ej. 7,3,1,0 — vacío usa el default del periodo "
                        + "(diario: 0 · semanal: 1,0 · mensual: 7,3,1,0). 0 = día del vencimiento.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(bottom = 6.dp),
                )
                PlanFormField(
                    reminderLeadDays,
                    { reminderLeadDays = it },
                    source?.effectiveReminderLeadDays ?: "7,3,1,0",
                )
                Spacer(Modifier.height(10.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Checkbox(checked = active, onCheckedChange = { active = it })
                    Text("Activo", color = AdminColors.Text, fontSize = 13.sp)
                }
            }
            }
            formError?.let {
                Text(it, color = AdminColors.Error, fontSize = 12.sp, modifier = Modifier.padding(top = 10.dp))
            }
            Spacer(Modifier.height(18.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        val n = name.trim()
                        if (n.isEmpty()) {
                            formError = "El nombre es obligatorio."
                            return@Button
                        }
                        val cents = moneyToCents(priceBs)
                        if (cents == null) {
                            formError = "Indica un precio válido en bolívares."
                            return@Button
                        }
                        val usdCents = moneyToCents(priceUsd) ?: 0L
                        formError = null
                        onSave(
                            AdminMembershipPlanUpsert(
                                name = n,
                                billingPeriod = period,
                                scheduleType = schedule,
                                priceCents = cents,
                                priceUsdCents = usdCents,
                                active = active,
                                serviceIds = selectedServiceIds.toList(),
                                reminderLeadDays = reminderLeadDays.trim().ifBlank { null },
                            ),
                        )
                    },
                    enabled = !saving && !loadingInitial,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    if (saving) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(18.dp),
                            color = AdminColors.OnBrand,
                            strokeWidth = 2.dp,
                        )
                    } else {
                        Text("Guardar")
                    }
                }
            }
            }
        }
    }
}

@Composable
private fun PlanFormLabel(text: String) {
    Text(
        text,
        fontSize = 13.sp,
        fontWeight = FontWeight.SemiBold,
        color = AdminColors.Text,
        modifier = Modifier.padding(bottom = 6.dp),
    )
}

@Composable
private fun PlanFormField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    focusRequester: FocusRequester? = null,
    onNext: (() -> Unit)? = null,
    onPrev: (() -> Unit)? = null,
    imeAction: ImeAction = ImeAction.Next,
    keyboardType: KeyboardType = KeyboardType.Text,
) {
    fun goNext() {
        onNext?.invoke()
    }
    fun goPrev() {
        onPrev?.invoke()
    }
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = Modifier
            .fillMaxWidth()
            .then(if (focusRequester != null) Modifier.focusRequester(focusRequester) else Modifier)
            .onPreviewKeyEvent { event ->
                if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                when (event.key) {
                    Key.Enter, Key.NumPadEnter -> {
                        goNext()
                        true
                    }
                    Key.Tab -> {
                        if (event.isShiftPressed) goPrev() else goNext()
                        true
                    }
                    Key.DirectionDown -> {
                        goNext()
                        true
                    }
                    Key.DirectionUp -> {
                        goPrev()
                        true
                    }
                    else -> false
                }
            },
        singleLine = true,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        keyboardOptions = KeyboardOptions(
            imeAction = imeAction,
            keyboardType = keyboardType,
        ),
        keyboardActions = KeyboardActions(
            onNext = { goNext() },
            onDone = { goNext() },
        ),
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}
